import { Injectable } from '@angular/core';
import { AppState } from '../models/app-state';
import { environment } from '../../environments/environment';
import { EnvConfig } from '../models/env-config';
import { STORAGE_KEYS } from '../components/dynamic-inputs/util/constants';
import { MessageService } from './message.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private storage: Storage;
  private session: Storage;
  private envConfig: any = environment;
  private appStateCache: AppState | null = null;
  private readonly APP_STATE_STORE = 'app_state';

  constructor(
    private messageService: MessageService,
    private dbService: NgxIndexedDBService
  ) {
    this.storage = window.localStorage;
    this.session = window.sessionStorage;
  }

  async initialize(): Promise<void> {
    console.log('[Storage] Inicializando SessionStorageService...');
    
    return new Promise((resolve) => {
      // Timeout de segurança de 3 segundos para não travar o bootstrap do Angular
      const safetyTimeout = setTimeout(() => {
        console.warn('[Storage] Timeout de 3s atingido! Resolvendo inicialização para não travar o app.');
        if (!this.appStateCache) {
          console.log('[Storage] Usando fallback síncrono para localStorage no timeout.');
          this.appStateCache = this.getLegacyAppState();
        }
        resolve();
      }, 3000);

      try {
        console.log('[Storage] Tentando carregar do IndexedDB...');
        this.dbService.getByID(this.APP_STATE_STORE, STORAGE_KEYS.APP_STATE).subscribe({
          next: (dbEntry: any) => {
            clearTimeout(safetyTimeout);
            console.log('[Storage] Dados carregados do IndexedDB com sucesso:', dbEntry ? 'Sim' : 'Não');
            
            if (dbEntry) {
              this.appStateCache = dbEntry.value;
              resolve();
            } else {
              console.log('[Storage] IndexedDB vazio. Verificando localStorage para migração...');
              const legacyState = this.getLegacyAppState();
              if (legacyState && Object.keys(legacyState).length > 0) {
                console.log('[Storage] Migrando dados do localStorage para IndexedDB...');
                this.appStateCache = legacyState;
                this.dbService.update(this.APP_STATE_STORE, {
                  id: STORAGE_KEYS.APP_STATE,
                  value: this.appStateCache
                }).subscribe({
                  next: () => {
                    console.log('[Storage] Migração concluída com sucesso. Removendo localStorage.');
                    this.storage.removeItem(STORAGE_KEYS.APP_STATE);
                    resolve();
                  },
                  error: (err) => {
                    console.error('[Storage] Erro ao salvar migração no IndexedDB:', err);
                    resolve(); // Resolve mesmo com erro para não travar
                  }
                });
              } else {
                console.log('[Storage] Nenhum dado prévio encontrado.');
                resolve();
              }
            }
          },
          error: (err) => {
            clearTimeout(safetyTimeout);
            console.error('[Storage] Erro ao ler do IndexedDB:', err);
            this.appStateCache = this.getLegacyAppState();
            resolve();
          }
        });
      } catch (error) {
        clearTimeout(safetyTimeout);
        console.error('[Storage] Exceção crítica durante initialize:', error);
        this.appStateCache = this.getLegacyAppState();
        resolve();
      }
    });
  }

  private getLegacyAppState(): AppState | null {
    const json = this.storage.getItem(STORAGE_KEYS.APP_STATE);
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  getEnvConfig(): EnvConfig {
    const objAssign = Object.assign(environment, this.getSessionEntry(STORAGE_KEYS.ENV_CONFIG)) as EnvConfig;

    const { production, baseUrlProd } = objAssign;
    if (production && baseUrlProd) {
      objAssign.baseUrl = objAssign.baseUrlProd;
    }
    return objAssign;
  }

  saveEnvConfig(config: EnvConfig): void {
    this.messageService.updateRewriteMessages(this.envConfig);
    this.saveSessionEntry(STORAGE_KEYS.ENV_CONFIG, config);
  }

  saveAppState(data: AppState): AppState {
    const currentState: AppState = this.getAppState();
    const state: AppState = {
      ...currentState,
      ...data,
    };
    
    // Atualiza o cache síncrono
    this.appStateCache = state;
    
    // Persiste no IndexedDB de forma assíncrona (background)
    this.dbService.update(this.APP_STATE_STORE, {
      id: STORAGE_KEYS.APP_STATE,
      value: state
    }).subscribe({
      error: (err) => console.error('Erro ao persistir AppState no IndexedDB:', err)
    });
    
    return state;
  }

  saveSessionEntry(entry: STORAGE_KEYS, data: any): void {
    this.session.setItem(entry, JSON.stringify(data));
  }

  getSessionEntry(entry: STORAGE_KEYS): any {
    return JSON.parse(this.session.getItem(entry) ?? '{}');
  }

  clearAppState(): void {
    this.appStateCache = null;
    this.storage.removeItem(STORAGE_KEYS.APP_STATE);
    this.dbService.delete(this.APP_STATE_STORE, STORAGE_KEYS.APP_STATE).subscribe();
  }

  getAppState(): AppState {
    return this.appStateCache || {} as AppState;
  }

  private get(key: string): any {
    const json = this.storage.getItem(key);
    if (!json) {
      return null;
    }
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
  
  saveTelefonePessoa(telefone: string): void {
      this.session.setItem('telefonePessoa', telefone);
  }

  getTelefonePessoa(): string | null {
      return this.session.getItem('telefonePessoa');
  }

}
